import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  FlightTakeoff as AssignIcon,
  PriorityHigh as UrgentIcon,
  Star as VipIcon,
  Warning as DgIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Timeline as TrackingIcon,
  CallSplit as SplitIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const Inventory = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [estaciones, setEstaciones] = useState([]);
  const [error, setError] = useState('');

  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    destination: '',
    status: '',
    priority: '',
    available_only: true,
  });

  // Selección para asignación masiva
  const [selected, setSelected] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [vuelos, setVuelos] = useState([]);
  const [selectedFlight, setSelectedFlight] = useState('');

  // Edición de AWB
  const [showAwbDialog, setShowAwbDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newAwb, setNewAwb] = useState('');
  const [savingAwb, setSavingAwb] = useState(false);

  // Merge de items sin AWB
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeAwb, setMergeAwb] = useState('');
  const [mergeNotes, setMergeNotes] = useState('');
  const [mergingItems, setMergingItems] = useState(false);

  // Split de items
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitItem, setSplitItem] = useState(null);
  const [splitParts, setSplitParts] = useState([{ pieces: '', weight: '', notes: '' }]);
  const [splittingItem, setSplittingItem] = useState(false);

  useEffect(() => {
    fetchInventory();
    fetchEstaciones();
  }, []);

  useEffect(() => {
    fetchVuelos();
  }, []);

  const fetchEstaciones = async () => {
    try {
      const res = await api.get(endpoints.estaciones);
      const data = res.data.data?.data || res.data.data || [];
      setEstaciones(data);
    } catch (error) {
      console.error('Error cargando estaciones:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.destination) params.append('destination', filters.destination);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.available_only) params.append('available_only', '1');

      const res = await api.get(`${endpoints.warehouse.list}?${params.toString()}`);
      setItems(res.data.data.items || []);
      setTotal(res.data.data.total || 0);
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const fetchVuelos = async () => {
    try {
      const res = await api.get(`${endpoints.vuelos.list}?limit=50`);
      const vuelosAbiertos = (res.data.data.vuelos || []).filter(v => !v.cerrado);
      setVuelos(vuelosAbiertos);
    } catch (error) {
      console.error('Error cargando vuelos:', error);
    }
  };

  const handleFilter = () => {
    fetchInventory();
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      destination: '',
      status: '',
      priority: '',
      available_only: true,
    });
    setTimeout(fetchInventory, 100);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Solo seleccionar items que tengan AWB y estén disponibles
      setSelected(items.filter(i => 
        (i.status === 'received' || i.status === 'partial') && i.awb
      ).map(i => i.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    const item = items.find(i => i.id === id);
    // No permitir seleccionar sin AWB
    if (!item?.awb) {
      setError('Este item no tiene AWB asignado. Debe agregar un AWB antes de poder asignarlo a un vuelo.');
      return;
    }
    setSelected(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAssignSelected = async () => {
    if (!selectedFlight) {
      setError('Seleccione un vuelo');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const id of selected) {
        const item = items.find(i => i.id === id);
        if (!item) continue;

        try {
          await api.post(endpoints.warehouse.loading, {
            flight_awb: selectedFlight,
            reception_id: id,
            pieces_assigned: item.pieces_remaining || item.pieces_received,
            weight_assigned: item.weight_remaining || item.weight_received,
          });
          successCount++;
        } catch (err) {
          console.error(`Error asignando ${item.reference}:`, err);
          errorCount++;
        }
      }

      setShowAssignDialog(false);
      setSelected([]);
      fetchInventory();

      if (successCount > 0) {
        setError('');
        alert(`${successCount} item(s) asignados exitosamente${errorCount > 0 ? `, ${errorCount} con error` : ''}`);
      }
    } catch (error) {
      setError('Error en asignación masiva');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      received: 'success',
      partial: 'warning',
      staged: 'info',
      loaded: 'primary',
      flown: 'default',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      received: 'Recibido',
      partial: 'Parcial',
      staged: 'Asignado',
      loaded: 'Cargado',
      flown: 'Volado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  };

  // Formatear AWB
  const formatAwbInput = (value) => {
    let clean = value.replace(/\D/g, '');
    if (clean.length > 11) clean = clean.slice(0, 11);
    if (clean.length > 3) {
      return clean.slice(0, 3) + '-' + clean.slice(3);
    }
    return clean;
  };

  // Abrir diálogo para editar AWB
  const handleOpenAwbDialog = (item) => {
    setEditingItem(item);
    setNewAwb(item.awb ? formatAwbInput(item.awb) : '');
    setShowAwbDialog(true);
  };

  // Guardar AWB
  const handleSaveAwb = async () => {
    if (!editingItem) return;
    
    const cleanAwb = newAwb.replace(/\D/g, '');
    if (cleanAwb && cleanAwb.length !== 11) {
      setError('El AWB debe tener 11 dígitos');
      return;
    }

    try {
      setSavingAwb(true);
      await api.put(endpoints.warehouse.reception, {
        id: editingItem.id,
        awb: cleanAwb || null,
      });
      
      setShowAwbDialog(false);
      setEditingItem(null);
      setNewAwb('');
      fetchInventory();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al actualizar AWB');
    } finally {
      setSavingAwb(false);
    }
  };

  // ===== MERGE FUNCTIONS =====
  
  // Toggle selección para merge (solo items sin AWB)
  const handleToggleMergeSelect = (id) => {
    const item = items.find(i => i.id === id);
    if (item?.awb) {
      setError('Solo se pueden fusionar items sin AWB asignado');
      return;
    }
    setSelectedForMerge(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Obtener items seleccionados para merge
  const mergeItems = items.filter(i => selectedForMerge.includes(i.id));

  // Calcular totales del merge
  const mergeTotals = mergeItems.reduce((acc, item) => ({
    pieces: acc.pieces + (item.pieces_remaining || item.pieces_received || 0),
    weight: acc.weight + parseFloat(item.weight_remaining || item.weight_received || 0),
  }), { pieces: 0, weight: 0 });

  // Verificar si todos los items seleccionados tienen el mismo destino
  const mergeDestinations = [...new Set(mergeItems.map(i => i.destination))];
  const canMerge = mergeDestinations.length === 1 && mergeItems.length >= 2;

  // Abrir diálogo de merge
  const handleOpenMerge = () => {
    if (mergeItems.length < 2) {
      setError('Seleccione al menos 2 items para fusionar');
      return;
    }
    if (mergeDestinations.length > 1) {
      setError('Solo se pueden fusionar items con el mismo destino');
      return;
    }
    // Combinar notas de los items
    const allNotes = mergeItems
      .map(i => i.notes)
      .filter(n => n)
      .join(', ');
    setMergeNotes(allNotes);
    setMergeAwb('');
    setShowMergeDialog(true);
  };

  // Ejecutar merge
  const handleMerge = async () => {
    if (mergeItems.length < 2) return;

    // Validar AWB si se ingresó
    const cleanAwb = mergeAwb.replace(/\D/g, '');
    if (cleanAwb && cleanAwb.length !== 11) {
      setError('El AWB debe tener 11 dígitos');
      return;
    }

    try {
      setMergingItems(true);
      await api.post(endpoints.warehouse.merge, {
        item_ids: selectedForMerge,
        awb: cleanAwb || null,
        notes: mergeNotes || null,
      });

      setShowMergeDialog(false);
      setSelectedForMerge([]);
      setMergeAwb('');
      setMergeNotes('');
      setError('');
      fetchInventory();
      alert(`${mergeItems.length} items fusionados exitosamente`);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al fusionar items');
    } finally {
      setMergingItems(false);
    }
  };

  // Cancelar selección de merge
  const handleCancelMerge = () => {
    setSelectedForMerge([]);
  };

  // ===== SPLIT FUNCTIONS =====

  // Abrir diálogo de split
  const handleOpenSplit = (item) => {
    if (item.pieces_remaining < 2 && item.pieces_received < 2) {
      setError('El item debe tener al menos 2 piezas para poder dividirlo');
      return;
    }
    setSplitItem(item);
    setSplitParts([{ pieces: '', weight: '', notes: '' }]);
    setShowSplitDialog(true);
  };

  // Agregar otra parte al split
  const addSplitPart = () => {
    setSplitParts([...splitParts, { pieces: '', weight: '', notes: '' }]);
  };

  // Remover parte del split
  const removeSplitPart = (index) => {
    if (splitParts.length === 1) return;
    setSplitParts(splitParts.filter((_, i) => i !== index));
  };

  // Actualizar parte del split
  const updateSplitPart = (index, field, value) => {
    setSplitParts(splitParts.map((part, i) => 
      i === index ? { ...part, [field]: value } : part
    ));
  };

  // Calcular totales y restante del split
  const splitTotals = splitParts.reduce((acc, part) => ({
    pieces: acc.pieces + (parseInt(part.pieces) || 0),
    weight: acc.weight + (parseFloat(part.weight) || 0),
  }), { pieces: 0, weight: 0 });

  const splitOriginal = splitItem ? {
    pieces: splitItem.pieces_remaining || splitItem.pieces_received || 0,
    weight: parseFloat(splitItem.weight_remaining || splitItem.weight_received || 0),
  } : { pieces: 0, weight: 0 };

  const splitRemaining = {
    pieces: splitOriginal.pieces - splitTotals.pieces,
    weight: splitOriginal.weight - splitTotals.weight,
  };

  // Validar split
  const canSplit = splitParts.length >= 1 && 
    splitParts.every(p => parseInt(p.pieces) > 0 && parseFloat(p.weight) > 0) &&
    splitRemaining.pieces >= 0 && splitRemaining.weight >= 0 &&
    splitTotals.pieces > 0;

  // Ejecutar split
  const handleSplit = async () => {
    if (!splitItem || !canSplit) return;

    // Validar que las partes no excedan el original
    if (splitRemaining.pieces < 0 || splitRemaining.weight < 0) {
      setError('Las partes exceden la cantidad disponible');
      return;
    }

    try {
      setSplittingItem(true);
      await api.post(endpoints.warehouse.split, {
        item_id: splitItem.id,
        parts: splitParts.map(p => ({
          pieces: parseInt(p.pieces),
          weight: parseFloat(p.weight),
          notes: p.notes || null,
        })),
      });

      setShowSplitDialog(false);
      setSplitItem(null);
      setSplitParts([{ pieces: '', weight: '', notes: '' }]);
      setError('');
      fetchInventory();
      alert(`Item dividido en ${splitParts.length + (splitRemaining.pieces > 0 ? 1 : 0)} partes exitosamente`);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al dividir item');
    } finally {
      setSplittingItem(false);
    }
  };

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Inventario de Depósito
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total} item(s) en inventario
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Botones de Merge */}
          {selectedForMerge.length > 0 && (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={handleOpenMerge}
                disabled={!canMerge}
              >
                Fusionar ({selectedForMerge.length})
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelMerge}
              >
                Cancelar
              </Button>
            </>
          )}
          {selected.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssignIcon />}
              onClick={() => setShowAssignDialog(true)}
            >
              Asignar ({selected.length})
            </Button>
          )}
          <Button 
            component={Link} 
            to="/warehouse/reception" 
            variant="contained" 
            color="success" 
            startIcon={<AddIcon />}
          >
            Nuevo Warehouse
          </Button>
          <Button component={Link} to="/warehouse" variant="contained" color="inherit" startIcon={<BackIcon />}>
            Volver
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Buscar AWB / Referencia / Nota"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                fullWidth
                size="small"
                placeholder="AWB, WH-XXX o referencia cliente"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Destino</InputLabel>
                <Select
                  value={filters.destination}
                  onChange={(e) => setFilters({ ...filters, destination: e.target.value })}
                  label="Destino"
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return <em style={{ color: '#999' }}>Todos</em>;
                    const est = estaciones.find(e => e.code === value);
                    return est ? `${est.code}` : value;
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estaciones.map(e => (
                    <MenuItem key={e.id} value={e.code}>
                      <strong>{e.code}</strong>&nbsp;- {e.Nombre || e.Pais || ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Estado</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Estado"
                  displayEmpty
                  renderValue={(value) => {
                    const labels = { received: 'Recibido', partial: 'Parcial', staged: 'Asignado' };
                    return value ? labels[value] : <em style={{ color: '#999' }}>Todos</em>;
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="received">Recibido</MenuItem>
                  <MenuItem value="partial">Parcial</MenuItem>
                  <MenuItem value="staged">Asignado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Prioridad</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                  label="Prioridad"
                  displayEmpty
                  renderValue={(value) => {
                    const labels = { normal: 'Normal', urgent: 'Urgente', vip: 'VIP' };
                    return value ? labels[value] : <em style={{ color: '#999' }}>Todas</em>;
                  }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="urgent">Urgente</MenuItem>
                  <MenuItem value="vip">VIP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleFilter} startIcon={<SearchIcon />}>
                  Filtrar
                </Button>
                <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                  Limpiar
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Toggle disponibles */}
          <Box sx={{ mt: 2 }}>
            <ToggleButtonGroup
              value={filters.available_only ? 'available' : 'all'}
              exclusive
              onChange={(e, value) => {
                setFilters({ ...filters, available_only: value === 'available' });
                setTimeout(fetchInventory, 100);
              }}
              size="small"
            >
              <ToggleButton value="available">Solo Disponibles</ToggleButton>
              <ToggleButton value="all">Todos</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < items.length}
                    checked={items.length > 0 && selected.length === items.filter(i => i.status === 'received' || i.status === 'partial').length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Referencia</TableCell>
                <TableCell>AWB</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell align="right">Piezas</TableCell>
                <TableCell align="right">Peso (kg)</TableCell>
                <TableCell>Naturaleza</TableCell>
                <TableCell>Prioridad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Recibido</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const isSelectable = item.status === 'received' || item.status === 'partial';
                const isSelected = selected.includes(item.id);
                const isSelectedForMerge = selectedForMerge.includes(item.id);
                const canSelectForMerge = isSelectable && !item.awb; // Solo items sin AWB pueden fusionarse

                return (
                  <TableRow
                    key={item.id}
                    hover
                    selected={isSelected || isSelectedForMerge}
                    sx={{
                      bgcolor: isSelectedForMerge ? 'warning.100' :
                               item.priority === 'vip' ? 'secondary.50' : 
                               item.priority === 'urgent' ? 'warning.50' : 'inherit'
                    }}
                  >
                    <TableCell padding="checkbox">
                      {isSelectable && (
                        item.awb ? (
                          // Items con AWB: checkbox para asignar a vuelo
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectOne(item.id)}
                          />
                        ) : (
                          // Items sin AWB: checkbox para merge
                          <Tooltip title="Seleccionar para fusionar">
                            <Checkbox
                              checked={isSelectedForMerge}
                              onChange={() => handleToggleMergeSelect(item.id)}
                              sx={{ 
                                color: 'warning.main',
                                '&.Mui-checked': { color: 'warning.main' }
                              }}
                            />
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontWeight="bold">{item.reference}</Typography>
                        {item.is_dangerous_goods == 1 && (
                          <Tooltip title="Mercancía Peligrosa">
                            <DgIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.awb ? (
                          <>
                            <Typography>{item.awb}</Typography>
                            <Tooltip title="Ver Tracking">
                              <IconButton 
                                size="small" 
                                component={Link}
                                to={`/warehouse/tracking?awb=${item.awb}`}
                                color="primary"
                              >
                                <TrackingIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar AWB">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenAwbDialog(item)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {item.notes && (
                              <Chip 
                                label={item.notes} 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                                sx={{ maxWidth: 150, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                title={item.notes}
                              />
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenAwbDialog(item)}
                            >
                              Agregar AWB
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.destination} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography>
                        {item.pieces_remaining || item.pieces_received}
                        {item.pieces_remaining !== item.pieces_received && (
                          <Typography variant="caption" color="text.secondary">
                            /{item.pieces_received}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {parseFloat(item.weight_remaining || item.weight_received).toLocaleString()}
                    </TableCell>
                    <TableCell>{item.nature || '-'}</TableCell>
                    <TableCell>
                      {item.priority === 'vip' && (
                        <Chip label="VIP" size="small" color="secondary" icon={<VipIcon />} />
                      )}
                      {item.priority === 'urgent' && (
                        <Chip label="Urgente" size="small" color="warning" icon={<UrgentIcon />} />
                      )}
                      {item.priority === 'normal' && (
                        <Typography variant="caption" color="text.secondary">Normal</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(item.status)}
                        size="small"
                        color={getStatusColor(item.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(item.received_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Ver detalles">
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/warehouse/detail/${item.id}`}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {/* Botón Split - solo para items con 2+ piezas */}
                        {(item.pieces_remaining || item.pieces_received) >= 2 && 
                         (item.status === 'received' || item.status === 'partial') && (
                          <Tooltip title="Dividir item">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleOpenSplit(item)}
                            >
                              <SplitIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No hay items en inventario</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog Asignar a Vuelo */}
      <Dialog open={showAssignDialog} onClose={() => setShowAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar {selected.length} item(s) a Vuelo</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel shrink>Seleccionar Vuelo</InputLabel>
            <Select
              value={selectedFlight}
              onChange={(e) => setSelectedFlight(e.target.value)}
              label="Seleccionar Vuelo"
              displayEmpty
              renderValue={(value) => {
                if (!value) return <em style={{ color: '#999' }}>Seleccionar vuelo</em>;
                const vuelo = vuelos.find(v => v.awb === value);
                return vuelo ? `${vuelo.awb} - ${vuelo.destino} (${vuelo.equipo_nombre})` : value;
              }}
            >
              <MenuItem value="" disabled>
                <em>Seleccionar vuelo</em>
              </MenuItem>
              {vuelos.map(v => (
                <MenuItem key={v.awb} value={v.awb}>
                  {v.awb} - {v.destino} ({v.equipo_nombre})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            Se asignarán todas las piezas disponibles de cada item seleccionado.
            El sistema validará destino y capacidad automáticamente.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleAssignSelected}
            variant="contained"
            disabled={!selectedFlight || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Asignar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar AWB */}
      <Dialog open={showAwbDialog} onClose={() => setShowAwbDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingItem?.awb ? 'Editar AWB' : 'Agregar AWB'}
        </DialogTitle>
        <DialogContent>
          {editingItem && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Referencia: <strong>{editingItem.reference}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Destino: <strong>{editingItem.destination}</strong>
              </Typography>
            </Box>
          )}
          <TextField
            label="Número de AWB"
            value={newAwb}
            onChange={(e) => setNewAwb(formatAwbInput(e.target.value))}
            fullWidth
            placeholder="123-45678900"
            helperText="Formato: 123-45678900 (11 dígitos). Requerido para asignar a vuelo."
            autoFocus
          />
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Importante:</strong> Los items sin AWB no pueden ser agregados al Loading List ni asignados a vuelos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAwbDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveAwb}
            variant="contained"
            color="primary"
            disabled={savingAwb}
            startIcon={savingAwb ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingAwb ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Fusionar Items */}
      <Dialog open={showMergeDialog} onClose={() => setShowMergeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'warning.contrastText' }}>
           Fusionar Items de Warehouse
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            Se fusionarán <strong>{mergeItems.length} items</strong> en uno solo.
            Las piezas y pesos se sumarán.
          </Alert>

          {/* Lista de items a fusionar */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Items a fusionar:</Typography>
            <TableContainer sx={{ maxHeight: 200 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Referencia</TableCell>
                    <TableCell>Nota</TableCell>
                    <TableCell align="right">Piezas</TableCell>
                    <TableCell align="right">Peso (kg)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mergeItems.map(item => (
                    <TableRow key={item.id}>
                      <TableCell><strong>{item.reference}</strong></TableCell>
                      <TableCell>{item.notes || '-'}</TableCell>
                      <TableCell align="right">{item.pieces_remaining || item.pieces_received}</TableCell>
                      <TableCell align="right">{parseFloat(item.weight_remaining || item.weight_received).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'grey.200' }}>
                    <TableCell colSpan={2}><strong>TOTAL</strong></TableCell>
                    <TableCell align="right"><strong>{mergeTotals.pieces}</strong></TableCell>
                    <TableCell align="right"><strong>{mergeTotals.weight.toFixed(2)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* AWB opcional */}
          <TextField
            label="AWB (opcional)"
            value={mergeAwb}
            onChange={(e) => setMergeAwb(formatAwbInput(e.target.value))}
            fullWidth
            placeholder="123-45678900"
            helperText="Si ya tiene un AWB asignado, ingréselo aquí"
            sx={{ mb: 2 }}
          />

          {/* Notas/Referencia */}
          <TextField
            label="Referencia / Notas"
            value={mergeNotes}
            onChange={(e) => setMergeNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Referencia del cliente, pedido, etc."
            helperText="Se combinaron las notas de los items originales"
          />

          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Atención:</strong> Esta acción no se puede deshacer. 
            Los items originales serán eliminados y se creará uno nuevo con los totales combinados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMergeDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleMerge}
            variant="contained"
            color="warning"
            disabled={mergingItems || mergeItems.length < 2}
            startIcon={mergingItems ? <CircularProgress size={16} /> : null}
          >
            {mergingItems ? 'Fusionando...' : `Fusionar ${mergeItems.length} Items`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Dividir Item */}
      <Dialog open={showSplitDialog} onClose={() => setShowSplitDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
          ✂️ Dividir Item de Warehouse
        </DialogTitle>
        <DialogContent>
          {splitItem && (
            <>
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                Dividiendo <strong>{splitItem.reference}</strong> - 
                {splitItem.awb && <> AWB: <strong>{splitItem.awb}</strong> -</>}
                {' '}Destino: <strong>{splitItem.destination}</strong>
              </Alert>

              {/* Info del item original */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Piezas Disponibles</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {splitOriginal.pieces}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Peso Disponible</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {splitOriginal.weight.toFixed(2)} kg
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Restante</Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    color={splitRemaining.pieces < 0 ? 'error.main' : 'success.main'}
                  >
                    {splitRemaining.pieces} pzs / {splitRemaining.weight.toFixed(2)} kg
                  </Typography>
                </Box>
              </Box>

              {/* Partes a crear */}
              <Typography variant="subtitle2" gutterBottom>
                Partes a crear (el restante quedará en el item original):
              </Typography>

              {splitParts.map((part, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mb: 2, 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 1,
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 70 }}>
                    Parte {index + 1}:
                  </Typography>
                  <TextField
                    label="Piezas"
                    type="number"
                    value={part.pieces}
                    onChange={(e) => updateSplitPart(index, 'pieces', e.target.value)}
                    size="small"
                    sx={{ width: 100 }}
                    inputProps={{ min: 1, max: splitOriginal.pieces }}
                  />
                  <TextField
                    label="Peso (kg)"
                    type="number"
                    value={part.weight}
                    onChange={(e) => updateSplitPart(index, 'weight', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ min: 0.1, step: 0.1, max: splitOriginal.weight }}
                  />
                  <TextField
                    label="Nota (opcional)"
                    value={part.notes}
                    onChange={(e) => updateSplitPart(index, 'notes', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="Referencia para esta parte"
                  />
                  {splitParts.length > 1 && (
                    <IconButton 
                      color="error" 
                      onClick={() => removeSplitPart(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={addSplitPart}
                sx={{ mb: 2 }}
              >
                + Agregar otra parte
              </Button>

              {/* Resumen */}
              <Box sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Resumen:</strong> Se crearán <strong>{splitParts.length}</strong> nuevos items
                  {splitRemaining.pieces > 0 && (
                    <> y quedarán <strong>{splitRemaining.pieces} piezas</strong> ({splitRemaining.weight.toFixed(2)} kg) en el original</>
                  )}
                </Typography>
              </Box>

              {splitRemaining.pieces < 0 && (
                <Alert severity="error">
                  Las piezas asignadas ({splitTotals.pieces}) exceden las disponibles ({splitOriginal.pieces})
                </Alert>
              )}
              {splitRemaining.weight < 0 && (
                <Alert severity="error">
                  El peso asignado ({splitTotals.weight.toFixed(2)} kg) excede el disponible ({splitOriginal.weight.toFixed(2)} kg)
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSplitDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleSplit}
            variant="contained"
            color="secondary"
            disabled={splittingItem || !canSplit}
            startIcon={splittingItem ? <CircularProgress size={16} /> : <SplitIcon />}
          >
            {splittingItem ? 'Dividiendo...' : `Dividir en ${splitParts.length + (splitRemaining.pieces > 0 ? 1 : 0)} partes`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;

